import H from '@here/maps-api-for-javascript/bin/mapsjs.bundle.harp';
import '@here/maps-api-for-javascript/bin/mapsjs-ui.css';
import { isMobileDevice } from '../helpers';
import { colors, tourColors } from '../../global/variables';
import { toBounds } from './MapCalculations';

export const MAX_MAP_ZOOM = 18;
export const MIN_MAP_ZOOM = 2;
export const UI_CONTROLS = {
  ZOOM: 'zoom',
  MAP_SETTINGS: 'mapsettings',
  SCALEBAR: 'scalebar',
};

const { unusedMarkerStroke, routeGradientStart, routeGradientEnd, unselectedTourColor } = colors;

export const getMapSettings = () =>
  isMobileDevice()
    ? { paddingTopLeft: [50, 0], paddingBottomRight: [80, 0] }
    : { paddingTopLeft: [450, 100], paddingBottomRight: [40, 40] };

export const animateFlyTo = {
  animation: true,
  duration: 1.111,
  easeLinearity: 0.69,
};

export const divideColors = (c1, c2, index, steps) => {
  if (!c1 || !c2 || !steps) return [];

  c1 = c1.match(/\d+/g).map(Number);
  c2 = c2.match(/\d+/g).map(Number);

  const colorArray = [];
  for (let i = 0; i < 3; i++) {
    colorArray[i] = Math.round(c1[i] + (index / steps) * (c2[i] - c1[i]));
  }

  return `rgb(${colorArray[0]},${colorArray[1]},${colorArray[2]})`;
};

export const getStyle = (feature, gradientRoutes, type, showOverlaps) => {
  const {
    properties: {
      routeId,
      mode,
      isStopsRequest,
      fromStop,
      totalStops,
      isUndesiredLine,
      colorGradient,
    },
  } = feature;

  const vehicleType = mode || type;
  const isPedestrian = vehicleType === 'pedestrian';
  const weight = 2;
  const dashArray = isPedestrian ? '2 6' : undefined;
  const color =
    isUndesiredLine && showOverlaps
      ? colorGradient
      : tourColors[routeId % tourColors.length].delivery.inner;
  const gradientColor = divideColors(routeGradientStart, routeGradientEnd, fromStop, totalStops);

  const routeColor = gradientRoutes && isStopsRequest ? gradientColor : color;

  return {
    opacity: 1,
    weight,
    color: routeColor,
    dashArray,
  };
};

export const getStyleUnselected = () => ({ opacity: 1, weight: 1.5, color: unselectedTourColor });
export const getStyleHidden = (feature, gradientRoutes, fleetType, showOverlaps) => {
  const base = getStyle(feature, gradientRoutes, fleetType, showOverlaps);
  return { ...base, opacity: 0.45, weight: 3, dashArray: '6 6' };
};
export const getStyleHitArea = () => ({ opacity: 0, weight: 20, fillOpacity: 0 });
export const getStyleOriginalLocationLine = () => ({
  opacity: 0.8,
  weight: 2,
  color: unusedMarkerStroke,
  dashArray: '4',
});
export const fitMapBounds = (map, bounds) => {
  if (!map) return;

  const latLngBoundsObj = toBounds(bounds);

  if (!latLngBoundsObj || !latLngBoundsObj.isValid()) {
    return;
  }

  map.fitBounds(latLngBoundsObj, getMapSettings(), animateFlyTo);
};

export const createBaseLayers = (engineType, language, ISO2Code) => {
  const providerOptions = {
    engineType,
    lg: language,
  };
  const platform = new H.service.Platform({
    apikey: process.env.REACT_APP_AK,
    ...providerOptions,
  });

  const omvService = platform.getOMVService({
    path: 'v2/vectortiles/core/mc',
    queryParams: {
      content: 'default',
      datasets: '(-pois)',
    },
  });

  const baseUrl = `https://js.api.here.com/v3/3.1/styles/harp/oslo`;
  const defaultLayers = platform.createDefaultLayers(providerOptions);
  const tileOptions = { min: MIN_MAP_ZOOM, max: MAX_MAP_ZOOM };

  const normalStyle = new H.map.render.harp.Style(`${baseUrl}/tko.normal.day.json`);
  const normalProvider = new H.service.omv.Provider(omvService, normalStyle, providerOptions);
  const normal = new H.map.layer.TileLayer(normalProvider, tileOptions);

  const hybridStyle = new H.map.render.harp.Style(`${baseUrl}/tko.lite.hybrid.day.json`);
  const hybridProvider = new H.service.omv.Provider(omvService, hybridStyle, providerOptions);
  const hybrid = new H.map.layer.TileLayer(hybridProvider);

  omvService.getPoliticalViews(
    (views) => {
      if (views.core.includes(ISO2Code?.toLowerCase())) {
        normalProvider.setPoliticalView(ISO2Code);
        hybridProvider.setPoliticalView(ISO2Code);
      }
    },
    () => {},
  );

  return { normal, hybrid, satellite: defaultLayers.raster.satellite.xbase };
};

export const createSettingsControl = (mapLayers) => {
  return new H.ui.MapSettingsControl({
    baseLayers: [
      { label: 'Map', layer: mapLayers.normal },
      { label: 'Satellite', layer: [mapLayers.satellite, mapLayers.hybrid] },
    ],
    alignment: H.ui.LayoutAlignment.BOTTOM_RIGHT,
  });
};

export const createBehaviour = (map) => {
  return new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
};

export const createUI = (mapLayers, map, mapSettingsControl) => {
  const scalebarControl = new H.ui.ScaleBar({
    alignment: H.ui.LayoutAlignment.BOTTOM_RIGHT,
  });
  const zoomControl = new H.ui.ZoomControl({
    alignment: H.ui.LayoutAlignment.RIGHT_BOTTOM,
  });

  const ui = new H.ui.UI(map);
  ui.addControl(UI_CONTROLS.ZOOM, zoomControl);
  ui.addControl(UI_CONTROLS.MAP_SETTINGS, mapSettingsControl);
  ui.addControl(UI_CONTROLS.SCALEBAR, scalebarControl);

  return ui;
};

export const createMap = (mapLayers, mapRef, engineType) => {
  const newMap = new H.Map(mapRef.current, mapLayers.normal, {
    zoom: 14,
    center: { lat: 52.531, lng: 13.38461 },
    engineType,
  });
  const behavior = createBehaviour(newMap);
  newMap._behavior = behavior;

  window.addEventListener('resize', () => newMap.getViewPort().resize());

  return newMap;
};

export const setUnitSystem = (mapUI, metricSystem) => {
  mapUI.setUnitSystem(
    metricSystem === 'metric' ? H.ui.UnitSystem.METRIC : H.ui.UnitSystem.IMPERIAL,
  );
};
